import * as React from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { Button, FormField, Input, PasswordInput, Select, Switch } from "Components/ui";
import type { RoleOptionsState } from "../data";

/** Values of the add and edit panels; add ignores `isActive`, edit ignores `password`. */
export interface UserFormValues {
  name: string;
  email: string;
  phoneNumber: string;
  userName: string;
  role: string;
  password: string;
  isActive: boolean;
}

export const EMPTY_USER_FORM: UserFormValues = {
  name: "",
  email: "",
  phoneNumber: "",
  userName: "",
  role: "",
  password: "",
  isActive: true,
};

// The server (services/rbacService.js) requires name, email, userName and role, and a password of
// at least 8 characters on create.
const sharedFields = {
  name: z.string().trim().min(1, "Enter the person's name."),
  email: z.string().trim().min(1, "Enter an email address.").email("Enter a valid email address."),
  userName: z.string().trim().min(1, "Choose a username."),
  role: z.string().min(1, "Pick a role."),
  isActive: z.boolean(),
};

export const addUserSchema = z.object({
  ...sharedFields,
  phoneNumber: z.string().trim().min(1, "Enter a WhatsApp number."),
  password: z.string().min(8, "Use at least 8 characters."),
});

export const editUserSchema = z.object({
  ...sharedFields,
  phoneNumber: z.string().trim(),
  password: z.string(),
});

interface UserFieldsProps {
  form: UseFormReturn<UserFormValues>;
  mode: "add" | "edit";
  roles: RoleOptionsState;
}

/** Profile and access fields shared by the add and edit panels. */
const UserFields: React.FC<UserFieldsProps> = ({ form, mode, roles }) => {
  const {
    register,
    control,
    formState: { errors, isSubmitting },
  } = form;

  let rolePlaceholder = "Select a role";
  if (roles.loading) {
    rolePlaceholder = "Loading roles…";
  } else if (roles.failed) {
    rolePlaceholder = "Roles didn't load";
  }

  return (
    <div className="space-y-6">
      <div className="space-y-5">
        <FormField label="Full name" required error={errors.name?.message}>
          <Input autoComplete="off" {...register("name")} />
        </FormField>

        <FormField label="Email" required error={errors.email?.message}>
          <Input type="email" autoComplete="off" spellCheck={false} {...register("email")} />
        </FormField>

        <FormField
          label="WhatsApp number"
          required={mode === "add"}
          error={errors.phoneNumber?.message}
          hint={
            mode === "add"
              ? "Sign-in codes are sent to this number on WhatsApp."
              : "Sign-in codes go here. Without a number this user can't sign in."
          }
        >
          <Input
            type="tel"
            inputMode="tel"
            autoComplete="off"
            placeholder="+91 98765 43210"
            {...register("phoneNumber")}
          />
        </FormField>

        <FormField
          label="Username"
          required
          error={errors.userName?.message}
          hint="Used with the password to sign in."
        >
          <Input
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            {...register("userName")}
          />
        </FormField>
      </div>

      <div className="space-y-5 border-t border-border pt-6">
        <FormField
          label="Role"
          required
          error={errors.role?.message || (roles.failed ? "Roles didn't load." : undefined)}
          hint="Decides what this person can see and change in SAP."
          labelAction={
            roles.failed ? (
              <Button variant="link" size="xs" onClick={roles.retry}>
                Try again
              </Button>
            ) : undefined
          }
        >
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <Select
                options={roles.options}
                value={field.value}
                onValueChange={field.onChange}
                placeholder={rolePlaceholder}
                disabled={isSubmitting || roles.loading || roles.options.length === 0}
              />
            )}
          />
        </FormField>

        {mode === "add" ? (
          <FormField
            label="Password"
            required
            error={errors.password?.message}
            hint="At least 8 characters."
          >
            <PasswordInput autoComplete="new-password" {...register("password")} />
          </FormField>
        ) : (
          <Controller
            control={control}
            name="isActive"
            render={({ field }) => (
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isSubmitting}
                label="Active"
                description="Suspended users can't sign in."
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

export default UserFields;
